import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { useMemo, useRef, useState } from 'react';

export interface LanguageSelectOption<T extends string = string> {
  value: T;
  label: string;
  nativeLabel?: string;
}

export interface LanguageSelectValue<T extends string = string> {
  selected: T[];
  primary: T | '';
}

export interface LanguageSelectProps<T extends string = string> {
  id?: string;
  options: LanguageSelectOption<T>[];
  selected: T[];
  primary?: T | '';
  onChange: (next: LanguageSelectValue<T>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export function LanguageSelect<T extends string = string>({
  id,
  options,
  selected,
  primary = '',
  onChange,
  placeholder = 'Add a language...',
  disabled,
  error,
}: LanguageSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const controlRef = useRef<HTMLDivElement>(null);

  const optionsByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  );

  const selectedOptions = selected
    .map((value) => optionsByValue.get(value))
    .filter((option): option is LanguageSelectOption<T> => Boolean(option));

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(trimmed) ||
        option.nativeLabel?.toLowerCase().includes(trimmed)
    );
  }, [options, query]);

  const toggle = (value: T) => {
    if (selected.includes(value)) {
      const nextSelected = selected.filter((v) => v !== value);
      let nextPrimary = primary;

      if (primary === value) {
        nextPrimary = nextSelected.length === 1 ? nextSelected[0] : '';
      }

      onChange({ selected: nextSelected, primary: nextPrimary });
      return;
    }

    onChange({
      selected: [...selected, value],
      primary: primary || value,
    });
  };

  return (
    <div className="w-full">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Anchor asChild>
          <div
            ref={controlRef}
            className={`relative flex w-full flex-wrap items-center gap-1 rounded-md border bg-white py-2 px-3 pr-8 text-left sm:text-sm ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'cursor-not-allowed bg-gray-50' : ''}`}
            data-testid={id ? `${id}-control` : 'language-select-control'}
          >
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className={`flex items-center gap-1 truncate rounded-md border px-2 py-1 text-sm ${
                  primary === option.value ? 'border-mint' : 'border-gray-300'
                }`}
                data-testid={`language-select-chip-${option.value}`}
              >
                {option.label}
                {primary === option.value && (
                  <span
                    className="text-xs text-mint"
                    data-testid={`language-select-primary-badge-${option.value}`}
                  >
                    primary
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  disabled={disabled}
                  onClick={() => toggle(option.value)}
                  className="text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed"
                >
                  <Cross1Icon className="h-3 w-3" />
                </button>
              </span>
            ))}

            <input
              id={id}
              type="text"
              className="min-w-[8rem] flex-1 border-0 p-0 text-sm focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50"
              placeholder={placeholder}
              value={query}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
              }}
              data-testid="language-select-filter"
            />

            <Popover.Trigger asChild>
              <button
                type="button"
                aria-label="Toggle language options"
                disabled={disabled}
                className="absolute inset-y-0 right-0 flex items-center pr-2 disabled:cursor-not-allowed"
                data-testid="language-select-toggle"
              >
                <ChevronDownIcon className="h-4 w-4 text-primary-700" />
              </button>
            </Popover.Trigger>
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="z-20 w-[var(--radix-popover-trigger-width)] rounded-md border border-gray-300 bg-white text-sm shadow-lg"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (controlRef.current?.contains(event.target as Node)) {
                event.preventDefault();
              }
            }}
            data-testid="language-select-panel"
          >
            {selectedOptions.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 p-2"
                data-testid="language-select-primary-bar"
              >
                <span className="text-xs text-gray-500">Primary Language:</span>
                {selectedOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={primary === option.value}
                    onClick={() =>
                      onChange({ selected, primary: option.value })
                    }
                    className={`rounded-full border px-3 py-1 text-xs ${
                      primary === option.value
                        ? 'border-mint bg-mint text-white'
                        : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-100'
                    }`}
                    data-testid={`language-select-primary-pill-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            <div className="max-h-60 overflow-auto py-1">
              {visibleOptions.map((option) => {
                const checkboxId = `language-select-option-${option.value}`;
                return (
                  <div
                    key={option.value}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50"
                  >
                    <CheckBox
                      id={checkboxId}
                      className="border-mint data-[state=checked]:bg-mint-100"
                      checked={selected.includes(option.value)}
                      onCheckedChange={() => toggle(option.value)}
                    />
                    <label
                      htmlFor={checkboxId}
                      className="flex-1 cursor-pointer text-gray-900"
                    >
                      {option.label}
                    </label>
                    {option.nativeLabel && (
                      <span className="text-gray-400">
                        {option.nativeLabel}
                      </span>
                    )}
                  </div>
                );
              })}

              {visibleOptions.length === 0 && (
                <p className="px-3 py-2 text-gray-500">No languages found</p>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
